import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateDivider } from "./DateDivider";
import { ChatBubble } from "./ChatBubble";
import { ChatBubbleGroup } from "./ChatBubbleGroup";
import { ChatEmptyState } from "./ChatEmptyState";
import type { ChatTimelineEntry } from "../../utils/chatGrouping";
import type { DocChatItem } from "@/lib/erp/docs";

type ChatTimelineProps = {
  entries: ChatTimelineEntry[];
  currentUserId: string | undefined;
  isLoading: boolean;
  isEmpty: boolean;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => Promise<void>;
  onDelete?: (commentName: string) => void;
  onReact?: (commentName: string, emoji: string) => void;
  onReply?: (item: DocChatItem) => void;
  registerMessageRef?: (name: string, el: HTMLElement | null) => void;
  scrollToMessage?: (commentName: string) => void;
};

export function ChatTimeline({
  entries,
  currentUserId,
  isLoading,
  isEmpty,
  hasMore,
  isFetchingNextPage,
  onLoadMore,
  onDelete,
  onReact,
  onReply,
  registerMessageRef,
  scrollToMessage,
}: ChatTimelineProps) {
  return (
    <>
      {/* Load older messages */}
      {hasMore && (
        <div className="flex justify-center pb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="h-10 px-4 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 touch-manipulation text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              "Load older messages"
            )}
          </Button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && isEmpty && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && isEmpty && <ChatEmptyState />}

      {/* Timeline entries */}
      <div className="space-y-4">
        {entries.map((entry) => {
          if (entry.type === "date-divider") {
            return <DateDivider key={`divider-${entry.date}`} label={entry.label} />;
          }

          if (entry.type === "activity") {
            return (
              <ChatBubble
                key={entry.item.name}
                item={entry.item}
                isMine={false}
                isFirstInGroup={true}
                isActivity={true}
              />
            );
          }

          // group
          return (
            <ChatBubbleGroup
              key={entry.items[0].name}
              group={entry}
              currentUserId={currentUserId}
              onDelete={onDelete}
              onReact={onReact}
              onReply={onReply}
              registerMessageRef={registerMessageRef}
              scrollToMessage={scrollToMessage}
            />
          );
        })}
      </div>
    </>
  );
}
